import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title:'',category:'',description:'',position:0,is_active:true });
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterCat, setFilterCat] = useState('');

  const fetch = useCallback(() => { api.get('/gallery').then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const resetForm = () => { setForm({ title:'',category:'',description:'',position:0,is_active:true }); setImageFile(null); setShowForm(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) { toast.error('Görsel seçin'); return; }
    setIsSubmitting(true);
    try {
      const fd = new FormData(); fd.append('file', imageFile);
      const r = await api.post('/uploads', fd);
      await api.post('/gallery', { ...form, image_path: r.data.path });
      toast.success('Eklendi'); resetForm(); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Silmek istediğinize emin misiniz?')) return; try { await api.delete(`/gallery/${id}`); toast.success('Silindi'); fetch(); } catch { toast.error('Hata'); } };

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];
  const filtered = filterCat ? items.filter(i => i.category === filterCat) : items;

  return (
    <div data-testid="admin-gallery-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Galeri Yönetimi</h1>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium"><Plus className="w-4 h-4" /> Görsel Ekle</button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">Yeni Görsel</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1.5">Başlık</label><input value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm" /></div>
              <div><label className="block text-sm font-medium mb-1.5">Kategori</label><input value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))} placeholder="İç Mekan, Dış Mekan..." className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm" /></div>
            </div>
            <div><label className="block text-sm font-medium mb-1.5">Görsel *</label>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary"><Upload className="w-4 h-4" /> Görsel Seç<input type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files[0])} /></label>
              <span className="text-xs text-muted-foreground ml-2">{imageFile?.name || ''}</span>
            </div>
            <div><label className="block text-sm font-medium mb-1.5">Açıklama</label><textarea value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} rows={2} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none" /></div>
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50">{isSubmitting ? 'Yükleniyor...' : 'Yükle'}</button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {categories.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => setFilterCat('')} className={`px-3 py-1.5 text-xs rounded-lg ${!filterCat ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'bg-secondary'}`}>Tümü</button>
          {categories.map(c => <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-1.5 text-xs rounded-lg ${filterCat===c ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'bg-secondary'}`}>{c}</button>)}
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      : filtered.length === 0 ? <div className="text-center py-10 text-muted-foreground">Henüz görsel yok</div>
      : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{filtered.map(g => (
        <div key={g.id} className="group relative rounded-xl overflow-hidden bg-card border border-border">
          <div className="aspect-square"><img src={getFileUrl(g.image_path)} alt={g.title} className="w-full h-full object-cover" /></div>
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
            {g.title && <span className="text-white text-xs font-medium">{g.title}</span>}
            <button onClick={() => handleDelete(g.id)} className="px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-xs"><Trash2 className="w-3 h-3 inline mr-1" />Sil</button>
          </div>
          {g.category && <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px]">{g.category}</div>}
        </div>
      ))}</div>}
    </div>
  );
}
